/**
 * Single fetch wrapper. All dashboard API calls go through this.
 *
 * Responsibilities:
 *  - Inject X-CSRF-Token on mutating requests (reads csrf_token cookie).
 *  - Auto-serialize plain-object JSON bodies; leave FormData alone.
 *  - Normalize 4xx/5xx into ApiError with .status/.code/.message/.fieldErrors.
 *  - Detect session expiry (401 + auth_required|session_expired) and call the
 *    registered handler so the app can redirect to /login.
 */

function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export class ApiError extends Error {
  constructor(status, code, message, fieldErrors, details) {
    super(message || code || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || null;
    this.details = details || null;
  }
}

let onSessionExpiredHandler = null;
export function setOnSessionExpired(handler) {
  onSessionExpiredHandler = handler;
}

export async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const needsCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (needsCsrf) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  let body = options.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body && !isFormData && typeof body === 'object' && !(body instanceof ArrayBuffer)) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    body = JSON.stringify(body);
  }

  const res = await fetch(path, {
    method,
    headers,
    body,
    credentials: options.credentials || 'same-origin',
    signal: options.signal,
  });

  if (res.status === 204) return null;

  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    const text = await res.text().catch(() => '');
    data = text ? { raw: text } : null;
  }

  if (!res.ok) {
    const code = data?.error || 'http_error';
    if (res.status === 401 && (code === 'auth_required' || code === 'session_expired')) {
      if (onSessionExpiredHandler) {
        try { onSessionExpiredHandler(); } catch { /* handler failures don't block */ }
      }
    }
    throw new ApiError(
      res.status,
      code,
      data?.message || null,
      data?.fields || data?.fieldErrors || null,
      data || null,
    );
  }

  return data;
}

/**
 * UPLOAD-WIRE A: the upload POST goes over XMLHttpRequest — fetch exposes no
 * upload progress, and the §24 countdown needs real transferred-byte numbers.
 * Semantics mirror apiFetch exactly (CSRF header, Accept json, same-origin
 * cookies, ApiError mapping incl. the session-expired hook); ONLY the upload
 * request uses this — polling and everything else stay on fetch.
 *
 * @param {string} path
 * @param {FormData} formData
 * @param {{onProgress?: ({loaded:number,total:number}) => void}} [opts]
 */
export function xhrUpload(path, formData, { onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.responseType = 'text';
    xhr.setRequestHeader('Accept', 'application/json');
    const csrf = readCookie('csrf_token');
    if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (e && e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
      };
    }
    xhr.onload = () => {
      let data = null;
      const ct = xhr.getResponseHeader('content-type') || '';
      if (ct.includes('application/json')) {
        try { data = JSON.parse(xhr.responseText); } catch { data = null; }
      } else if (xhr.responseText) {
        data = { raw: xhr.responseText };
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.status === 204 ? null : data);
        return;
      }
      const code = data?.error || 'http_error';
      if (xhr.status === 401 && (code === 'auth_required' || code === 'session_expired')) {
        if (onSessionExpiredHandler) {
          try { onSessionExpiredHandler(); } catch { /* handler failures don't block */ }
        }
      }
      reject(new ApiError(xhr.status, code, data?.message || null,
        data?.fields || data?.fieldErrors || null, data || null));
    };
    xhr.onerror = () => reject(new TypeError('network request failed'));
    xhr.onabort = () => reject(new TypeError('network request aborted'));
    xhr.send(formData);
  });
}

export const api = {
  signup: (body) => apiFetch('/api/auth/signup', { method: 'POST', body }),
  verifyEmail: (body) => apiFetch('/api/auth/verify-email', { method: 'POST', body }),
  resendVerification: (body) =>
    apiFetch('/api/auth/resend-verification', { method: 'POST', body }),
  login: (body) => apiFetch('/api/auth/login', { method: 'POST', body }),
  logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),
  passwordResetRequest: (body) =>
    apiFetch('/api/auth/password-reset-request', { method: 'POST', body }),
  passwordResetConfirm: (body) =>
    apiFetch('/api/auth/password-reset-confirm', { method: 'POST', body }),
  me: (opts) => apiFetch('/api/me', { method: 'GET', ...opts }),
  upload: (formData, opts) =>
    apiFetch('/api/upload', { method: 'POST', body: formData, ...opts }),
  // UPLOAD-WIRE A: XHR variant with byte progress for the §24 countdown.
  // Test seam: __CW_FORCE_FETCH_UPLOAD routes through api.upload (the fetch
  // path) so jsdom suites' fetch stubs AND api.upload spies keep intercepting.
  uploadWithProgress: (formData, opts = {}) =>
    (globalThis.__CW_FORCE_FETCH_UPLOAD
      ? api.upload(formData)
      : xhrUpload('/api/upload', formData, opts)),
  // v3.20 §2: queue state for the §24 queue-aware countdown estimate.
  uploadQueueStatus: (opts) =>
    apiFetch('/api/upload/queue-status', { method: 'GET', ...opts }),
  // LAUNCH-CLOSE B: the poll half of the job-ticket upload contract.
  jobStatus: (jobId, opts) =>
    apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, { method: 'GET', ...opts }),
  // RENDER-SCALE: the full-analysis GET /api/report/:id endpoint was retired
  // (zero callers; analysis.json no longer persisted) — the report page fetches
  // /api/report/:id/dashboard (bounded projection) directly in ReportView.jsx.
  getRecentReports: (opts) =>
    apiFetch('/api/reports/recent', { method: 'GET', ...opts }),

  // Organization dashboards (9B-2)
  getOrgUsage: (opts) =>
    apiFetch('/api/organization/usage', { method: 'GET', ...opts }),
  getScoreTrends: (opts) =>
    apiFetch('/api/organization/score-trends', { method: 'GET', ...opts }),
  getPortfolio: (opts) =>
    apiFetch('/api/organization/portfolio', { method: 'GET', ...opts }),
  getActivity: (opts) =>
    apiFetch('/api/organization/activity', { method: 'GET', ...opts }),

  // Platform-owner oversight (owner-era) — read-only; 403 owner_required for non-owners.
  getAdminOverview: (opts) =>
    apiFetch('/api/admin/overview', { method: 'GET', ...opts }),
  getAdminUsers: (params, opts) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.pageSize) q.set('page_size', String(params.pageSize));
    const qs = q.toString();
    return apiFetch(`/api/admin/users${qs ? `?${qs}` : ''}`, { method: 'GET', ...opts });
  },
  // BACKUP-NET (v3.35): owner-only backups surface.
  getAdminBackups: (opts) =>
    apiFetch('/api/admin/backups', { method: 'GET', ...opts }),
  runAdminBackup: (opts) =>
    apiFetch('/api/admin/backups/run', { method: 'POST', ...opts }),

  // Team management (9B-2)
  listMembers: (opts) =>
    apiFetch('/api/team/members', { method: 'GET', ...opts }),
  listInvitations: (opts) =>
    apiFetch('/api/team/invitations', { method: 'GET', ...opts }),
  createInvitation: (body) =>
    apiFetch('/api/team/invitations', { method: 'POST', body }),
  revokeInvitation: (id) =>
    apiFetch(`/api/team/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  removeMember: (id) =>
    apiFetch(`/api/team/members/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  getInvitation: (token, opts) =>
    apiFetch(`/api/team/invitations/${encodeURIComponent(token)}`, { method: 'GET', ...opts }),
  acceptInvitation: (token, body) =>
    apiFetch(`/api/team/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST', body }),

  // Settings (10A D3)
  getSettingsProfile: (opts) =>
    apiFetch('/api/settings/profile', { method: 'GET', ...opts }),
  patchSettingsProfile: (body) =>
    apiFetch('/api/settings/profile', { method: 'PATCH', body }),
  getSettingsCompany: (opts) =>
    apiFetch('/api/settings/company', { method: 'GET', ...opts }),
  patchSettingsCompany: (body) =>
    apiFetch('/api/settings/company', { method: 'PATCH', body }),
  changePassword: (body) =>
    apiFetch('/api/settings/password', { method: 'POST', body }),

  // Settings — branding (10A D3 sub-piece 2)
  getSettingsBranding: (opts) =>
    apiFetch('/api/settings/branding', { method: 'GET', ...opts }),
  patchSettingsBranding: (body) =>
    apiFetch('/api/settings/branding', { method: 'PATCH', body }),
  uploadSettingsBrandingLogo: (formData, opts) =>
    apiFetch('/api/settings/branding/logo', { method: 'PUT', body: formData, ...opts }),
  deleteSettingsBrandingLogo: () =>
    apiFetch('/api/settings/branding/logo', { method: 'DELETE' }),

  // Settings — danger zone / account deletion (10A D3 sub-piece 3)
  getSettingsDangerBranch: (opts) =>
    apiFetch('/api/settings/danger/branch', { method: 'GET', ...opts }),
  postDangerBranchA: (body) =>
    apiFetch('/api/settings/danger/branch-a/transfer-and-delete', { method: 'POST', body }),
  postDangerBranchB: (body) =>
    apiFetch('/api/settings/danger/branch-b/delete-with-grace', { method: 'POST', body }),
  postDangerBranchC: (body) =>
    apiFetch('/api/settings/danger/branch-c/leave-org', { method: 'POST', body }),
  postDangerBranchD: (body) =>
    apiFetch('/api/settings/danger/branch-d/delete-expired', { method: 'POST', body }),
};
